import { FormModal, SubmitProps } from '@/components/form-modal.js';
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import {
  bulkUpdateStudentGroupReassignmentRequestStatus,
  getPendingStudentGroupReassignmentRequests,
} from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import {
  PendingStudentGroupReassignmentRequest,
  StudentGroupReassignmentRequestStatus,
  StudentGroupReassignmentRequestStatusLabelMap,
} from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { z } from 'zod';

const formSchema = z.object({});

type BulkUpdateReassignmentStatusItemProps = {
  reassignmentRequests: PendingStudentGroupReassignmentRequest[];
  status: StudentGroupReassignmentRequestStatus;
  onSuccess?: () => void;
  trigger?: ReactNode;
};

const BulkUpdateReassignmentStatusItem = ({
  reassignmentRequests,
  status,
  onSuccess,
  trigger = (
    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
      {StudentGroupReassignmentRequestStatusLabelMap[status]}
    </DropdownMenuItem>
  ),
}: BulkUpdateReassignmentStatusItemProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({ mutationFn: bulkUpdateStudentGroupReassignmentRequestStatus });
  const statusLabel = StudentGroupReassignmentRequestStatusLabelMap[status];

  const onSubmit = ({ closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    return mutateAsync({
      reassignmentRequestIds: reassignmentRequests.map((request) => request.id),
      status,
    })
      .then(({ success }) => {
        toast({
          title: `${success} reassignment request(s) were updated to ${statusLabel}`,
        });
        queryClient.invalidateQueries(getPendingStudentGroupReassignmentRequests());
        closeModal();
        onSuccess?.();
      })
      .catch((error) => {
        toast({
          title: 'There was an error. Please try again.',
          description: airServerErrorFrom(error)?.userFacingMessage,
          variant: 'destructive',
        });
      });
  };

  return (
    <FormModal
      modalTitle={`Update reassignment status to ${statusLabel}`}
      formSchema={formSchema}
      trigger={trigger}
      onSubmit={onSubmit}
      confirmBtnLabel="Confirm"
      submitButtonProps={{
        disabled: reassignmentRequests.length === 0,
      }}
    >
      {() => (
        <div className="my-6">
          {reassignmentRequests.length === 0 ? (
            <div>None of the selected reassignment requests are eligible.</div>
          ) : (
            <div>
              {`Are you sure you want to change the status to ${statusLabel} for ${reassignmentRequests.length} reassignment request(s)?`}
            </div>
          )}
        </div>
      )}
    </FormModal>
  );
};

export interface BulkUpdateReassignmentStatusMenuProps {
  reassignmentRequests: PendingStudentGroupReassignmentRequest[];
  onSuccess?: () => void;
}

export const BulkUpdateReassignmentStatusMenu = ({
  reassignmentRequests,
  onSuccess,
}: BulkUpdateReassignmentStatusMenuProps) => {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>Reassignment status</DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <BulkUpdateReassignmentStatusItem
          reassignmentRequests={reassignmentRequests}
          status={StudentGroupReassignmentRequestStatus.requested}
          onSuccess={onSuccess}
        />
        <BulkUpdateReassignmentStatusItem
          reassignmentRequests={reassignmentRequests}
          status={StudentGroupReassignmentRequestStatus.processing}
          onSuccess={onSuccess}
        />
        <BulkUpdateReassignmentStatusItem
          reassignmentRequests={reassignmentRequests}
          status={StudentGroupReassignmentRequestStatus.resolved}
          onSuccess={onSuccess}
        />
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};
